import React from 'react';
import {
  Box,
  Divider,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

const UniversityAiWorkloadSettings = ({
  config,
  marking,
  normalizeReasoningEffort,
  onMarkingChange,
  onMarkingParameterChange,
  onRealtimeChange,
  onTranscriptionChange,
  realtime,
  reasoningEffortOptions,
  transcription,
  verbosityDefault = 'medium',
  voices = [],
}) => (
  <>
    <Paper elevation={0} sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
          <TextField
            select
            label="Realtime provider"
            value={realtime.provider}
            onChange={onRealtimeChange('provider')}
            sx={{ minWidth: 200 }}
            disabled={Boolean(realtime.useGlobalCredentials)}
          >
            <MenuItem value="openai">OpenAI</MenuItem>
            <MenuItem value="azure">Azure OpenAI</MenuItem>
            <MenuItem value="gemini">Gemini Live</MenuItem>
          </TextField>
        </Stack>
      {realtime.provider === 'openai' && (
        <TextField
          label="Parent OpenAI API key"
            type="password"
            value={realtime.parentApiKey}
            onChange={onRealtimeChange('parentApiKey')}
            disabled={Boolean(realtime.useGlobalCredentials)}
            helperText={
              realtime.useGlobalCredentials
                ? 'Using global OpenAI key'
                : (config?.realtime?.openaiKeyConfigured
                  ? 'Leave blank to keep the existing key'
                  : 'Provide your institution API key')
            }
          />
        )}
        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
          <Stack spacing={1}>
            <Typography variant="subtitle2">Unity consultation behaviour</Typography>
            <FormControlLabel
              control={(
                <Switch
                  checked={Boolean(realtime.allowInterruptions)}
                  onChange={onRealtimeChange('allowInterruptions')}
                  disabled={Boolean(realtime.useGlobalCredentials)}
                />
              )}
              label="Allow students to interrupt the AI patient"
            />
            <FormControlLabel
              control={(
                <Switch
                  checked={Boolean(realtime.toolCallingEnabled)}
                  onChange={onRealtimeChange('toolCallingEnabled')}
                  disabled={Boolean(realtime.useGlobalCredentials)}
                />
              )}
              label="Enable AI tool calling"
            />
            <Typography variant="caption" color="text.secondary">
              Tool calling powers live learn-mode overlays and patient animation reactions in Unity.
            </Typography>
          </Stack>
        </Box>
        {realtime.provider === 'gemini' && (
          <Stack spacing={2}>
            <TextField
              label="Parent Gemini API key"
              type="password"
              value={realtime.parentApiKey}
              onChange={onRealtimeChange('parentApiKey')}
              disabled={Boolean(realtime.useGlobalCredentials)}
              helperText={
                realtime.useGlobalCredentials
                  ? 'Using global Gemini key'
                  : (config?.realtime?.geminiKeyConfigured
                    ? 'Leave blank to keep the existing key'
                    : 'Provide your institution Gemini API key')
              }
            />
            <FormControlLabel
              control={(
                <Switch
                  checked={Boolean(realtime.proactiveAudio)}
                  onChange={onRealtimeChange('proactiveAudio')}
                  disabled={Boolean(realtime.useGlobalCredentials)}
                />
              )}
              label="Enable proactive audio"
            />
            <FormControlLabel
              control={(
                <Switch
                  checked={Boolean(realtime.affectiveDialog)}
                  onChange={onRealtimeChange('affectiveDialog')}
                  disabled={Boolean(realtime.useGlobalCredentials)}
                />
              )}
              label="Enable affective dialog"
            />
            <TextField
              select
              label="Reasoning effort"
              value={normalizeReasoningEffort(realtime.reasoningEffort)}
              onChange={onRealtimeChange('reasoningEffort')}
              disabled={Boolean(realtime.useGlobalCredentials)}
              helperText="Controls Gemini Live thinking; off omits thinking configuration."
              sx={{ maxWidth: 260 }}
            >
              {reasoningEffortOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </TextField>
            <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
              <Stack spacing={2}>
                <FormControlLabel
                  control={(
                    <Switch
                      checked={Boolean(realtime.contextWindowCompressionEnabled)}
                      onChange={onRealtimeChange('contextWindowCompressionEnabled')}
                      disabled={Boolean(realtime.useGlobalCredentials)}
                    />
                  )}
                  label="Enable context window compression"
                />
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField
                    label="Compression trigger tokens"
                    type="number"
                    value={realtime.contextWindowCompressionTriggerTokens}
                    onChange={onRealtimeChange('contextWindowCompressionTriggerTokens')}
                    disabled={Boolean(realtime.useGlobalCredentials) || !realtime.contextWindowCompressionEnabled}
                    helperText="Blank uses Gemini default trigger."
                    sx={{ minWidth: 240 }}
                    inputProps={{ min: 1, step: 1 }}
                  />
                  <TextField
                    label="Compression target tokens"
                    type="number"
                    value={realtime.contextWindowCompressionTargetTokens}
                    onChange={onRealtimeChange('contextWindowCompressionTargetTokens')}
                    disabled={Boolean(realtime.useGlobalCredentials) || !realtime.contextWindowCompressionEnabled}
                    helperText="Blank uses Gemini default target."
                    sx={{ minWidth: 240 }}
                    inputProps={{ min: 1, step: 1 }}
                  />
                </Stack>
              </Stack>
            </Box>
          </Stack>
        )}
        {realtime.provider === 'azure' && (
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Azure Realtime uses WebRTC only. Virtual Medic keeps this API key on the server and mints a short-lived client secret for each Unity connection.
            </Typography>
            <TextField
              label="Azure endpoint"
              value={realtime.azureEndpoint}
              onChange={onRealtimeChange('azureEndpoint')}
              helperText="Base Azure OpenAI endpoint URL (e.g., https://your-resource.openai.azure.com)"
              disabled={Boolean(realtime.useGlobalCredentials)}
            />
            <TextField
              label="Azure OpenAI API key"
              type="password"
              value={realtime.azureApiKey}
              onChange={onRealtimeChange('azureApiKey')}
              disabled={Boolean(realtime.useGlobalCredentials)}
              helperText={
                realtime.useGlobalCredentials
                  ? 'Using global Azure OpenAI key'
                  : (config?.realtime?.azureKeyConfigured
                    ? 'Leave blank to keep the existing key'
                    : 'Provide the Azure OpenAI key for this tenant')
              }
            />
          </Stack>
        )}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField
            label="Realtime model"
            value={realtime.realtimeModel}
            onChange={onRealtimeChange('realtimeModel')}
            sx={{ minWidth: 240 }}
            helperText={
              realtime.provider === 'azure'
                ? 'Enter the Azure Realtime deployment name.'
                : (config?.allowedRealtimeModels?.length
                ? `Enter the exact realtime model id (e.g., ${config.allowedRealtimeModels.slice(0, 3).join(', ')})`
                : 'Enter the exact realtime model id configured for your tenant.')
            }
            autoComplete="off"
            inputProps={{ spellCheck: 'false' }}
          />
          {realtime.provider === 'gemini' ? (
            <TextField
              label="Voice"
              value={realtime.voice}
              onChange={onRealtimeChange('voice')}
              sx={{ minWidth: 240 }}
              helperText="Gemini prebuilt voice name (for example: Kore, Puck, Charon)."
            />
          ) : (
            <TextField
              select
              label="Voice"
              value={realtime.voice}
              onChange={onRealtimeChange('voice')}
              sx={{ minWidth: 240 }}
            >
              {voices.map((voice) => (
                <MenuItem key={voice} value={voice}>{voice}</MenuItem>
              ))}
            </TextField>
          )}
        </Stack>
      </Stack>
    </Paper>
    
    <Paper elevation={0} sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
          <TextField
            select
            label="Marking provider"
            value={marking.provider}
            onChange={onMarkingChange('provider')}
            sx={{ minWidth: 200 }}
            disabled={Boolean(marking.useGlobalCredentials)}
          >
            <MenuItem value="openai">OpenAI</MenuItem>
            <MenuItem value="azure">Azure OpenAI</MenuItem>
          </TextField>
        </Stack>
        {marking.provider === 'openai' && (
          <TextField
            label="Parent OpenAI API key"
            type="password"
            value={marking.parentApiKey}
            onChange={onMarkingChange('parentApiKey')}
            disabled={Boolean(marking.useGlobalCredentials)}
            helperText={
              marking.useGlobalCredentials
                ? 'Using global OpenAI key'
                : (config?.marking?.openaiKeyConfigured
                  ? 'Leave blank to keep the existing key'
                  : 'Provide your institution API key')
            }
          />
        )}
        {marking.provider === 'azure' && (
          <Stack spacing={2}>
            <TextField
              label="Azure endpoint"
              value={marking.azureEndpoint}
              onChange={onMarkingChange('azureEndpoint')}
              helperText="Base Azure OpenAI endpoint URL (e.g., https://your-resource.openai.azure.com) or full responses endpoint (https://your-resource.openai.azure.com/openai/responses?api-version=...)"
              disabled={Boolean(marking.useGlobalCredentials)}
            />
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                label="Deployment id"
                value={marking.azureDeploymentId}
                onChange={onMarkingChange('azureDeploymentId')}
                helperText="Azure deployment id for your marking model (optional if using full responses endpoint)"
                sx={{ minWidth: 200 }}
                disabled={Boolean(marking.useGlobalCredentials)}
              />
              <TextField
                label="API version"
                value={marking.azureApiVersion}
                onChange={onMarkingChange('azureApiVersion')}
                helperText="Azure API version (e.g., 2025-04-01-preview). Optional if included in endpoint."
                sx={{ minWidth: 200 }}
                disabled={Boolean(marking.useGlobalCredentials)}
              />
            </Stack>
            <TextField
              label="Resource name (optional)"
              value={marking.azureResourceName}
              onChange={onMarkingChange('azureResourceName')}
              disabled={Boolean(marking.useGlobalCredentials)}
            />
            <TextField
              label="Azure OpenAI API key"
              type="password"
              value={marking.azureApiKey}
              onChange={onMarkingChange('azureApiKey')}
              disabled={Boolean(marking.useGlobalCredentials)}
              helperText={
                marking.useGlobalCredentials
                  ? 'Using global Azure OpenAI key'
                  : (config?.marking?.azureKeyConfigured
                    ? 'Leave blank to keep the existing key'
                    : 'Provide the Azure OpenAI key for this tenant')
              }
            />
            <FormControlLabel
              control={(
                <Switch
                  checked={Boolean(marking.azureUseActiveDirectory)}
                  onChange={onMarkingChange('azureUseActiveDirectory')}
                  disabled={Boolean(marking.useGlobalCredentials)}
                />
              )}
              label="Use Azure Active Directory (access token authentication)"
            />
          </Stack>
        )}
        <TextField
          label="Marking model"
          value={marking.markingModel}
          onChange={onMarkingChange('markingModel')}
          sx={{ minWidth: 240 }}
          helperText={
            (config?.allowedMarkingModels?.length
              ? `Enter the exact responses model id (e.g., ${config.allowedMarkingModels.slice(0, 3).join(', ')})`
              : 'Enter the exact responses model used for marking.')
          }
          autoComplete="off"
          inputProps={{ spellCheck: 'false' }}
        />
        <Divider />
        <Typography variant="subtitle2">Marking parameters</Typography>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField
            label="Temperature"
            type="number"
            value={marking.parameters?.temperature ?? ''}
            onChange={onMarkingParameterChange('temperature')}
            inputProps={{ min: 0, max: 2, step: 0.1 }}
            helperText="0 for deterministic scoring"
            sx={{ minWidth: 200 }}
          />
          <TextField
            label="Max output tokens"
            type="number"
            value={marking.parameters?.maxOutputTokens ?? ''}
            onChange={onMarkingParameterChange('maxOutputTokens')}
            helperText="Optional cap on response tokens"
            sx={{ minWidth: 200 }}
          />
          <TextField
            label="Top P"
            type="number"
            value={marking.parameters?.topP ?? ''}
            onChange={onMarkingParameterChange('topP')}
            inputProps={{ min: 0, max: 1, step: 0.05 }}
            helperText="Nucleus sampling"
            sx={{ minWidth: 200 }}
          />
        </Stack>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <TextField
            label="Frequency penalty"
            type="number"
            value={marking.parameters?.frequencyPenalty ?? ''}
            onChange={onMarkingParameterChange('frequencyPenalty')}
            inputProps={{ min: -2, max: 2, step: 0.1 }}
            sx={{ minWidth: 200 }}
          />
          <TextField
            label="Presence penalty"
            type="number"
            value={marking.parameters?.presencePenalty ?? ''}
            onChange={onMarkingParameterChange('presencePenalty')}
            inputProps={{ min: -2, max: 2, step: 0.1 }}
            sx={{ minWidth: 200 }}
          />
          <TextField
            label="Reasoning effort"
            value={marking.parameters?.reasoningEffort || ''}
            onChange={onMarkingParameterChange('reasoningEffort')}
            helperText="Optional: reasoning effort for GPT-5 models"
            sx={{ minWidth: 240 }}
          />
        </Stack>
        <TextField
          select
          label="Verbosity"
          value={marking.parameters?.verbosity || verbosityDefault}
          onChange={onMarkingParameterChange('verbosity')}
          helperText="Controls feedback length (low = very concise)"
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="low">Low</MenuItem>
          <MenuItem value="medium">Medium</MenuItem>
          <MenuItem value="high">High</MenuItem>
        </TextField>
      </Stack>
    </Paper>
    
    <Paper elevation={0} sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Typography variant="subtitle2">Input transcription override (optional)</Typography>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
          <FormControlLabel
            control={(
              <Switch
                checked={Boolean(transcription.enabled)}
                onChange={onTranscriptionChange('enabled')}
              />
            )}
            label="Enable tenant transcription override"
          />
          <TextField
            select
            label="Provider"
            value={transcription.provider}
            onChange={onTranscriptionChange('provider')}
            sx={{ minWidth: 200 }}
            disabled={Boolean(transcription.useGlobalCredentials)}
          >
            <MenuItem value="openai">OpenAI</MenuItem>
            <MenuItem value="azure">Azure OpenAI</MenuItem>
          </TextField>
        </Stack>
    
        <TextField
          label="Transcription model"
          value={transcription.model}
          onChange={onTranscriptionChange('model')}
          helperText="Default: gpt-4o-transcribe"
          disabled={!transcription.enabled}
          sx={{ minWidth: 240 }}
        />
    
        {transcription.provider === 'openai' && (
          <TextField
            label="OpenAI API key"
            type="password"
            value={transcription.parentApiKey}
            onChange={onTranscriptionChange('parentApiKey')}
            disabled={!transcription.enabled || Boolean(transcription.useGlobalCredentials)}
            helperText={
              transcription.useGlobalCredentials
                ? 'Using global OpenAI transcription key'
                : (config?.transcription?.openaiKeyConfigured
                  ? 'Leave blank to keep the existing key'
                  : 'Provide the API key for transcription requests')
            }
          />
        )}
    
        {transcription.provider === 'azure' && (
          <Stack spacing={2}>
            <TextField
              label="Azure endpoint"
              value={transcription.azureEndpoint}
              onChange={onTranscriptionChange('azureEndpoint')}
              helperText="Base Azure endpoint or full audio/transcriptions endpoint URL"
              disabled={!transcription.enabled || Boolean(transcription.useGlobalCredentials)}
            />
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                label="Deployment id"
                value={transcription.azureDeploymentId}
                onChange={onTranscriptionChange('azureDeploymentId')}
                helperText="Optional if endpoint already includes deployment."
                sx={{ minWidth: 200 }}
                disabled={!transcription.enabled || Boolean(transcription.useGlobalCredentials)}
              />
              <TextField
                label="API version"
                value={transcription.azureApiVersion}
                onChange={onTranscriptionChange('azureApiVersion')}
                helperText="Optional if api-version is already in endpoint."
                sx={{ minWidth: 200 }}
                disabled={!transcription.enabled || Boolean(transcription.useGlobalCredentials)}
              />
            </Stack>
            <TextField
              label="Resource name (optional)"
              value={transcription.azureResourceName}
              onChange={onTranscriptionChange('azureResourceName')}
              disabled={!transcription.enabled || Boolean(transcription.useGlobalCredentials)}
            />
            <TextField
              label="Azure API key"
              type="password"
              value={transcription.azureApiKey}
              onChange={onTranscriptionChange('azureApiKey')}
              disabled={!transcription.enabled || Boolean(transcription.useGlobalCredentials)}
              helperText={
                transcription.useGlobalCredentials
                  ? 'Using global Azure transcription key'
                  : (config?.transcription?.azureKeyConfigured
                    ? 'Leave blank to keep the existing key'
                    : 'Provide the Azure API key for transcription')
              }
            />
            <FormControlLabel
              control={(
                <Switch
                  checked={Boolean(transcription.azureUseActiveDirectory)}
                  onChange={onTranscriptionChange('azureUseActiveDirectory')}
                  disabled={!transcription.enabled || Boolean(transcription.useGlobalCredentials)}
                />
              )}
              label="Use Azure Active Directory"
            />
          </Stack>
        )}
    
        <Typography variant="body2" color="text.secondary">
          Applies to user-input transcript text only. Realtime voice generation remains unchanged.
        </Typography>
      </Stack>
    </Paper>
    
  </>
);

export default UniversityAiWorkloadSettings;
